import React, { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

import type { PublishTimestampEntry } from '@/store/analytics/types';
import type { TrafficToggles } from '@/components/analytics/types';
import IconAiMagic from '@/assets/icons/icon-ai-magic.svg?react';
import IconTradeUp from '@/assets/icons/icon-trade-up.svg?react';
import IconTradeDown from '@/assets/icons/icon-trade-down.svg?react';

export interface ChartDataPoint {
  /** Date label shown on X axis */
  date: string;
  views?: number;
  uv?: number;
  cv?: number;
}

type LineKey = 'views' | 'uv' | 'cv';

const LINE_CONFIG: Record<
  LineKey,
  {
    label: string;
    stroke: string;
    legendColor: string;
    gradientId: string;
    gradientStops: Array<{ offset: string; color: string; opacity: number }>;
  }
> = {
  views: {
    label: 'Views',
    stroke: '#d4d4d8',
    legendColor: '#d4d4d8',
    gradientId: 'gradViews',
    gradientStops: [
      { offset: '0%', color: '#9ca3af', opacity: 0.1 },
      { offset: '100%', color: '#ffffff', opacity: 0.02 },
    ],
  },
  uv: {
    label: 'Unique Visitors',
    stroke: '#818cf8',
    legendColor: '#818cf8',
    gradientId: 'gradUv',
    gradientStops: [
      { offset: '0%', color: '#4f46e5', opacity: 0.2 },
      { offset: '100%', color: '#c4b5fd', opacity: 0.2 },
    ],
  },
  cv: {
    label: 'Conversions',
    stroke: '#34d399',
    legendColor: '#34d399',
    gradientId: 'gradCv',
    gradientStops: [
      { offset: '0%', color: '#34d399', opacity: 0.1 },
      { offset: '100%', color: '#a7f3d0', opacity: 0.1 },
    ],
  },
};

const KEYS = Object.keys(LINE_CONFIG) as LineKey[];

/* ─────────────────────────── Tooltip ─────────────────────────── */

interface CustomTooltipPayload {
  name: string;
  value: number;
  color: string;
  dataKey: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: CustomTooltipPayload[];
  label?: string;
  chartData: ChartDataPoint[];
  hoveredTimestamp: PublishTimestampEntry | null;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  chartData,
  hoveredTimestamp,
}) => {
  if (!active || !payload?.length) return null;

  const isTimestamp = hoveredTimestamp !== null;

  const entry = payload[0];
  const currentIndex = chartData.findIndex((d) => d.date === label);
  const currentValue = entry.value;
  const dataKey = entry.dataKey as keyof ChartDataPoint;
  const previousValue =
    currentIndex > 0 ? (chartData[currentIndex - 1][dataKey] as number | undefined) : undefined;

  const delta =
    previousValue !== undefined && currentValue !== undefined ? currentValue - previousValue : null;
  const percentChange =
    previousValue && previousValue !== 0
      ? ((currentValue - previousValue) / previousValue) * 100
      : null;
  const isPositive = delta !== null && delta >= 0;

  const TrendIcon = isPositive ? IconTradeUp : IconTradeDown;

  return (
    <div className="relative w-40 overflow-hidden rounded-xl">
      {/* Data tooltip */}
      <div
        className={clsx(
          'bg-neutral-0 flex w-40 flex-col items-start justify-center gap-1 rounded-xl py-3 pr-3 pl-4 shadow-[0px_4px_12px_0px_rgba(20,27,36,0.04)] outline-1 -outline-offset-1 outline-neutral-100 transition-all duration-200 ease-out',
          isTimestamp
            ? 'pointer-events-none absolute inset-0 scale-95 opacity-0'
            : 'scale-100 opacity-100',
        )}
      >
        <div className="inline-flex w-full items-center justify-between">
          <span className="text-[10px] leading-4 font-normal text-neutral-400">{label}</span>
          {percentChange !== null && (
            <div className="flex h-6 items-center gap-0.5 rounded-lg bg-neutral-50 p-1.5 outline-1 -outline-offset-1 outline-neutral-100">
              <span className="text-[10.29px] leading-3 font-medium text-neutral-500">
                {percentChange > 0 ? '+' : ''}
                {Math.round(percentChange)}%
              </span>
              <TrendIcon
                className={clsx('h-3.5 w-3.5', isPositive ? 'text-green-500' : 'text-red-500')}
              />
            </div>
          )}
        </div>
        <span className="w-full text-xs leading-5 font-semibold text-neutral-900">
          {currentValue.toLocaleString()}
        </span>
        {delta !== null && (
          <div className="inline-flex items-start gap-1">
            <span
              className={clsx(
                'text-[10px] leading-4 font-medium',
                isPositive ? 'text-green-500' : 'text-red-500',
              )}
            >
              {isPositive ? '+' : ''}
              {delta}
            </span>
            <span className="text-[10px] leading-4 font-medium text-neutral-400">
              vs previous day
            </span>
          </div>
        )}
      </div>

      {/* Timestamp tooltip */}
      <div
        className={clsx(
          'bg-neutral-0 flex w-40 flex-col gap-3 rounded-xl py-3 pr-3 pl-4 shadow-[0px_4px_12px_0px_rgba(20,27,36,0.04)] outline-1 -outline-offset-1 outline-neutral-100 transition-all duration-200 ease-out',
          isTimestamp
            ? 'scale-100 opacity-100'
            : 'pointer-events-none absolute inset-0 scale-95 opacity-0',
        )}
      >
        {hoveredTimestamp && (
          <>
            <div className="flex flex-col">
              <span className="text-[10px] leading-4 font-normal text-neutral-400">
                {hoveredTimestamp.fullDate}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              {hoveredTimestamp.items.map((item) => (
                <div key={item.name} className="inline-flex items-center gap-1">
                  <span className="text-neutral-1000 text-[10px] leading-5 font-semibold">
                    {item.name}
                  </span>
                  {item.isAiGenerated && (
                    <span className="inline-flex h-6 items-center gap-0.5 rounded-lg p-1.5 outline-1 -outline-offset-1 outline-neutral-200">
                      <IconAiMagic className="h-3.5 w-3.5" />
                      <span className="text-[10.29px] leading-3 font-medium text-neutral-500">
                        AI
                      </span>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────── Legend ─────────────────────────── */

/** Maps sidebar traffic toggle keys to chart line keys */
const TRAFFIC_TO_LINE: Record<string, LineKey> = {
  views: 'views',
  uniqueVisitors: 'uv',
  conversions: 'cv',
};

interface ChartLegendProps {
  traffic: TrafficToggles;
  hasTimestamps: boolean;
}

const ChartLegend: React.FC<ChartLegendProps> = ({ traffic, hasTimestamps }) => (
  <div className="flex items-center gap-3">
    {hasTimestamps && traffic.publishTimestamps && (
      <div className="flex items-center gap-1">
        <div
          className="h-3 w-3 rounded-[3px] outline-[0.5px] -outline-offset-[0.5px] outline-neutral-100"
          style={{ backgroundColor: '#f97316' }}
        />
        <span className="text-[10px] leading-4 font-medium text-neutral-400">Timestamps</span>
      </div>
    )}
    {KEYS.map((key) => {
      const { label, legendColor } = LINE_CONFIG[key];
      const trafficKey = Object.entries(TRAFFIC_TO_LINE).find(([, v]) => v === key)?.[0];
      const isActive = trafficKey ? traffic[trafficKey as keyof TrafficToggles] : true;
      if (!isActive) return null;
      return (
        <div key={key} className="flex items-center gap-1">
          <div
            className="h-3 w-3 rounded-[3px] outline-[0.5px] -outline-offset-[0.5px] outline-neutral-100"
            style={{ backgroundColor: legendColor }}
          />
          <span className="text-[10px] leading-4 font-medium text-neutral-400">{label}</span>
        </div>
      );
    })}
  </div>
);

/* ─────────────────────────── Loading skeleton ─────────────────────────── */

const LoadingSkeleton: React.FC = () => (
  <div className="flex h-full animate-pulse flex-col gap-3 p-4">
    <div className="flex justify-end gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="h-3 w-3 rounded-[3px] bg-neutral-100" />
          <div className="h-2 w-16 rounded bg-neutral-100" />
        </div>
      ))}
    </div>
    <div className="flex-1 rounded-lg bg-neutral-50" />
  </div>
);

/* ─────────────────────────── Empty state ─────────────────────────── */

const EmptyState: React.FC = () => (
  <div className="flex h-full flex-col items-center justify-center">
    <span className="text-sm leading-5 font-medium text-neutral-400">Waiting for first data</span>
  </div>
);

/* ─────────────────────────── Custom X-axis tick ─────────────────────────── */

/* ─────────────────────────── Custom X-axis tick ─────────────────────────── */

const createEdgeTick = (totalTicks: number) => {
  const EdgeTick = ({ x = 0, y = 0, index = 0, payload }: Record<string, unknown>) => {
    const isFirst = index === 0;
    const isLast = index === totalTicks - 1;
    if (!isFirst && !isLast) return <g />;

    const value = (payload as { value?: string } | undefined)?.value;
    return (
      <text
        x={x as number}
        y={(y as number) + 12}
        textAnchor={isFirst ? 'start' : 'end'}
        fontSize={10}
        fontFamily="Inter, sans-serif"
        fill="var(--color-neutral-400)"
      >
        {value}
      </text>
    );
  };
  return EdgeTick;
};

/* ─────────────────────────── Main component ─────────────────────────── */

interface AnalyticsChartProps {
  data?: ChartDataPoint[];
  isLoading?: boolean;
  publishTimestamps?: PublishTimestampEntry[];
  traffic?: TrafficToggles;
  className?: string;
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  data = [],
  isLoading = false,
  publishTimestamps = [],
  traffic = { views: true, uniqueVisitors: true, conversions: true, publishTimestamps: true },
  className,
}) => {
  const [hoveredTimestamp, setHoveredTimestamp] = useState<PublishTimestampEntry | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const activeLines = new Set<LineKey>(
    KEYS.filter((key) => {
      const trafficKey = Object.entries(TRAFFIC_TO_LINE).find(([, v]) => v === key)?.[0];
      return trafficKey ? traffic[trafficKey as keyof TrafficToggles] : true;
    }),
  );

  const hasData = data.length > 0;
  const totalTicks = data.length;

  const renderTooltip = useCallback(
    function ChartTooltip(props: Record<string, unknown>) {
      return (
        <CustomTooltip
          {...(props as Omit<CustomTooltipProps, 'chartData' | 'hoveredTimestamp'>)}
          chartData={data}
          hoveredTimestamp={hoveredTimestamp}
        />
      );
    },
    [data, hoveredTimestamp],
  );

  const visibleTimestamps = traffic.publishTimestamps ? publishTimestamps : [];

  const handleChartMouseMove = useCallback(
    (
      state: { activeLabel?: string | number; activeCoordinate?: { x: number; y: number } } | null,
    ) => {
      if (!state?.activeLabel || !state?.activeCoordinate) return;
      const label = String(state.activeLabel);
      const matchedTs = visibleTimestamps.find((ts) => ts.label === label);
      if (matchedTs) {
        setHoveredTimestamp(matchedTs);
      } else {
        setHoveredTimestamp(null);
      }
    },
    [visibleTimestamps],
  );

  const handleChartMouseLeave = useCallback(() => {
    setHoveredTimestamp(null);
  }, []);

  return (
    <div
      ref={chartContainerRef}
      className={clsx(
        'bg-neutral-0 relative flex h-80 flex-col overflow-hidden rounded-xl shadow-[0px_4px_12px_0px_rgba(20,27,36,0.04)] outline-1 -outline-offset-1 outline-neutral-100 [&_*:focus]:outline-none [&_.recharts-surface]:outline-none',
        className,
      )}
    >
      {isLoading ? (
        <LoadingSkeleton />
      ) : !hasData ? (
        <EmptyState />
      ) : (
        <>
          <div className="flex shrink-0 items-center justify-end px-4 pt-3">
            <ChartLegend traffic={traffic} hasTimestamps={publishTimestamps.length > 0} />
          </div>

          <div className="relative min-h-0 flex-1 px-4 pb-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data}
                margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
                onMouseMove={handleChartMouseMove}
                onMouseLeave={handleChartMouseLeave}
              >
                <defs>
                  {KEYS.map((key) => {
                    const { gradientId, gradientStops } = LINE_CONFIG[key];
                    return (
                      <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        {gradientStops.map((stop, i) => (
                          <stop
                            key={i}
                            offset={stop.offset}
                            stopColor={stop.color}
                            stopOpacity={stop.opacity}
                          />
                        ))}
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid
                  strokeDasharray="0"
                  stroke="var(--color-neutral-100)"
                  vertical
                  horizontal={false}
                />
                <XAxis
                  dataKey="date"
                  interval={0}
                  tick={createEdgeTick(totalTicks)}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--color-neutral-100)' }}
                />
                <Tooltip
                  content={renderTooltip}
                  cursor={
                    hoveredTimestamp
                      ? false
                      : { stroke: 'var(--color-neutral-200)', strokeWidth: 1 }
                  }
                />
                {KEYS.map((key) => {
                  if (!activeLines.has(key)) return null;
                  const { stroke, gradientId } = LINE_CONFIG[key];
                  return (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={stroke}
                      strokeWidth={2}
                      fill={`url(#${gradientId})`}
                      dot={false}
                      activeDot={{
                        r: 4,
                        strokeWidth: 2,
                        stroke: 'var(--color-neutral-0)',
                        fill: stroke,
                      }}
                    />
                  );
                })}
                {visibleTimestamps.map((ts) => (
                  <ReferenceLine
                    key={`pub-${ts.label}`}
                    x={ts.label}
                    stroke="#f97316"
                    strokeWidth={1.5}
                    strokeDasharray={hoveredTimestamp?.label === ts.label ? '0' : '4 3'}
                    label={{
                      value: '●',
                      position: 'top',
                      fill: '#f97316',
                      fontSize: 8,
                    }}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};
